import axios from 'axios';
import { toast } from 'react-toastify';

/**
 * axiosInstance — Single centralized Axios instance for the whole app.
 *
 * What it does automatically:
 *  1. Sets the base URL from environment variable
 *  2. Attaches the auth token to every request
 *  3. Handles 401 (unauthorized) and 403 (forbidden) responses globally
 */
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// ─── Request Interceptor ──────────────────────────────────────────────────────
// Attach the token from localStorage to every outgoing request
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor ─────────────────────────────────────────────────────
// Handle common HTTP errors globally so you don't repeat this logic everywhere
axiosInstance.interceptors.response.use(
  (response) => response, // Success — just pass through

  (error) => {
    const status = error?.response?.status;
    const serverMessage = error?.response?.data?.message;

    if (status === 401) {
      // Skip redirect for login endpoints — wrong credentials should show error, not reload
      const isLoginRequest = error?.config?.url?.includes('/Auth/login') || error?.config?.url?.includes('/CustomerAuth/login');
      if (!isLoginRequest) {
        localStorage.removeItem('authToken');
        toast.error('Session expired. Please login again.');
        window.location.href = '/';
      }
    } else if (status === 403) {
      toast.error('You do not have permission to perform this action.');
    } else if (status === 404) {
      if (!error?.config?.suppress404Toast) {
        toast.error('Requested resource was not found.');
      }
    } else if (status === 409) {
      toast.error(serverMessage || 'Conflict: This record already exists.');
    } else if (status === 500) {
      toast.error('Server error. Please try again later.');
    } else if (!status) {
      // Network error (no response at all)
      toast.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
